// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * University Administrator sign-in (**ADR-0075**).
 *
 * The browser holds no credential and stores none. The password lives in
 * component state for the duration of one submit and is cleared the moment the
 * request returns — success or failure. Nothing here touches `localStorage` or
 * `sessionStorage`; the workspace lint configuration rejects such access
 * outright, and `SEC2-019` prohibits a browser-stored credential regardless.
 *
 * There is no default or placeholder credential. The password is generated at
 * deployment, shown once, and never written down by this application.
 *
 * Every rejection is one message. The BFF answers a wrong username, a wrong
 * password, an exhausted rate limit, and an unreadable Secret identically, and
 * telling them apart here would undo that.
 */

import { ApiProblemError, useApiClients, useSession } from '@eai/ui-shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useRef, useState, type FormEvent, type ReactNode } from 'react';

/** The one message every credential failure produces. */
export const GENERIC_REFUSAL = 'Sign-in failed. Check the username and password and try again.';

/** Shown when the service could not answer at all. */
export const SERVICE_UNAVAILABLE =
  'The sign-in service is unavailable. This is not a problem with your credentials — try again shortly.';

/**
 * Decide what a failed sign-in should say.
 *
 * A 503 or 504 is a service fault and is reported as one: telling an operator
 * their password is wrong when the service is down sends them to rotate a
 * credential that was never the problem.
 */
export function describeLoginFailure(error: unknown): string {
  if (error instanceof ApiProblemError && (error.status === 503 || error.status === 504)) {
    return SERVICE_UNAVAILABLE;
  }
  if (error instanceof ApiProblemError && error.status === 0) {
    return SERVICE_UNAVAILABLE;
  }
  return GENERIC_REFUSAL;
}

/** Sign-in screen shown whenever the session is unauthenticated. */
export function BootstrapLoginScreen(): ReactNode {
  const clients = useApiClients();
  const { refresh } = useSession();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const usernameRef = useRef<HTMLInputElement | null>(null);

  const submit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      setFailure(null);
      setSubmitting(true);
      try {
        await clients.session.signIn({ username, password });
        // Cleared before the session is re-read, so the value is gone from
        // component state at the earliest possible moment.
        setPassword('');
        setUsername('');
        await refresh();
      } catch (error) {
        setPassword('');
        setFailure(describeLoginFailure(error));
        usernameRef.current?.focus();
      } finally {
        setSubmitting(false);
      }
    },
    [clients, password, refresh, username],
  );

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper variant="outlined" sx={{ p: 4 }} component="main">
        <Typography variant="h1" sx={{ fontSize: '1.5rem', mb: 1 }}>
          University Control Center
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Sign in as the University Administrator to continue.
        </Typography>

        {failure === null ? null : (
          <Alert severity="error" sx={{ mb: 2 }} role="alert" data-testid="login-error">
            {failure}
          </Alert>
        )}

        <Box component="form" onSubmit={(event) => void submit(event)} noValidate>
          <Stack spacing={2}>
            <TextField
              label="Username"
              value={username}
              onChange={(event) => {
                setUsername(event.target.value);
              }}
              autoComplete="username"
              required
              inputRef={usernameRef}
              slotProps={{ htmlInput: { maxLength: 128 } }}
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
              autoComplete="current-password"
              required
              slotProps={{ htmlInput: { maxLength: 256 } }}
            />
            <Box>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
}
